import { Card } from "@/components/flexkit/Card";
import { Switch } from "@/components/Switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { Text } from "@/components/podkit/typography/Text";
import {
    useGetAutoTopupSettings,
    useUpdateAutoTopupSettings,
    type PlainAutoTopupSettings,
} from "@/queries/organization-queries";
import { creditsToDollars } from "@/routes/settings/organization/billing/billing-utils";
import { formatError } from "@/utils/errors";
import type { FC } from "react";

const TOPUP_AMOUNT_OPTIONS = [
    { value: 100, label: "100 OCUs" },
    { value: 200, label: "200 OCUs" },
    { value: 400, label: "400 OCUs" },
    { value: 1000, label: "1,000 OCUs" },
] as const;

// Outer component gates on loading so that the inner component's
// initial render always has the real server values.
export const AutoTopupSettings: FC = () => {
    const { data: settings, isPending: isLoadingSettings } = useGetAutoTopupSettings();

    if (isLoadingSettings || !settings) {
        return null;
    }

    return <AutoTopupSettingsInner settings={settings} />;
};

const AutoTopupSettingsInner: FC<{ settings: PlainAutoTopupSettings }> = ({ settings }) => {
    const updateSettings = useUpdateAutoTopupSettings();
    const { toast } = useToast();

    const save = (patch: Partial<PlainAutoTopupSettings>) => {
        const next: PlainAutoTopupSettings = {
            enabled: patch.enabled ?? settings.enabled,
            amountCredits: patch.amountCredits ?? (settings.amountCredits || 100),
            maxDailyTopups: settings.maxDailyTopups,
        };

        updateSettings.mutate(next, {
            onSuccess: () => {
                toast({
                    title: "Auto top-up settings updated",
                });
            },
            onError: (error: unknown) => {
                toast({
                    title: "Failed to update auto top-up settings",
                    description: formatError(error),
                });
            },
        });
    };

    const switchState = updateSettings.isPending ? "pending" : settings.enabled ? "checked" : "unchecked";

    return (
        <Card className="bg-surface-glass p-6" variant="bordered">
            <div className="mb-6">
                <h2 className="text-lg font-semibold text-content-primary">Auto Top-up</h2>
                <Text className="text-sm text-content-secondary">
                    Automatically add credits when your balance drops below 20 OCUs.
                </Text>
            </div>

            <div className="flex flex-col gap-6">
                <div className="flex items-center gap-4">
                    <Switch
                        state={switchState}
                        onToggle={(checked) => save({ enabled: checked })}
                        disabled={updateSettings.isPending}
                        id="auto-topup-toggle"
                        data-testid="auto-topup-toggle"
                        aria-label="Enable auto top-up"
                    />
                    <label className="cursor-pointer font-medium text-content-primary" htmlFor="auto-topup-toggle">
                        Enable auto top-up
                    </label>
                </div>

                {settings.enabled && (
                    <div className="flex flex-col gap-4">
                        <div className="flex flex-col gap-2">
                            <label
                                htmlFor="topup-amount-select"
                                className="text-sm font-medium text-content-primary"
                            >
                                Top-up amount
                            </label>
                            <Select
                                value={String(settings.amountCredits || 100)}
                                onValueChange={(v) => save({ amountCredits: Number(v) })}
                                disabled={updateSettings.isPending}
                            >
                                <SelectTrigger id="topup-amount-select" loading={false}>
                                    <SelectValue placeholder="Select amount" />
                                </SelectTrigger>
                                <SelectContent>
                                    {TOPUP_AMOUNT_OPTIONS.map((option) => (
                                        <SelectItem
                                            key={option.value}
                                            value={String(option.value)}
                                            className="px-4 py-2 text-base"
                                        >
                                            <div>
                                                {option.label} – ${creditsToDollars(option.value).toFixed(2)}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <Text className="text-sm text-content-secondary">
                                This amount will be charged to your default payment method each time auto top-up
                                triggers.
                            </Text>
                        </div>
                    </div>
                )}
            </div>
        </Card>
    );
};
